// crewbit-admin/src/components/money/DocumentCard.tsx
//
// Spec: §InvoiceDetail.7.2 document card. Line items table + subtotal / tax /
// total + notes. Header carries doc-type label + invoice_number + status pill.
// Optional pills for source_quote_id / project_id / work_order_id linkage.

import { palette, spacing, typography } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import { fromRpcLineItem, type AdminLineItem } from '../../lib/invoiceMutations';
import type { Invoice } from '../../types';

interface Props {
  invoice: Invoice;
}

const TABULAR: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

function LinkPill({ label }: { label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 8px', borderRadius: 999,
        background: palette.g100, color: palette.g700,
        fontSize: typography.size.micro, fontWeight: typography.weight.medium,
      }}
    >
      {label}
    </span>
  );
}

export function DocumentCard({ invoice }: Props) {
  const items: AdminLineItem[] = (invoice.line_items ?? []).map((li) =>
    fromRpcLineItem(li as unknown as Record<string, unknown>),
  );
  const docLabel = invoice.is_sales_receipt ? 'Sales receipt' : 'Invoice';
  const taxRatePct = invoice.tax_rate != null ? Math.round((invoice.tax_rate as number) * 1000) / 10 : null;

  const linkPills: string[] = [];
  if (invoice.source_quote_id) linkPills.push('From quote');
  if (invoice.project_id) linkPills.push('Linked to project');
  if (invoice.work_order_id) linkPills.push('Linked to job');

  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: 12,
        padding: spacing.md,
        marginBottom: spacing.md,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.sm }}>
        <div>
          <div style={{ fontSize: typography.size.caption, color: palette.g500, fontWeight: typography.weight.medium }}>
            {docLabel}
          </div>
          <div style={{ fontSize: typography.size.heading, fontWeight: typography.weight.semibold, color: palette.g800, marginTop: 2 }}>
            {invoice.invoice_number}
          </div>
        </div>
        {linkPills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
            {linkPills.map((p) => <LinkPill key={p} label={p} />)}
          </div>
        )}
      </div>

      {invoice.client_name && (
        <div style={{ fontSize: typography.size.caption, color: palette.g600, marginBottom: spacing.md }}>
          Bill to <span style={{ color: palette.g800, fontWeight: typography.weight.medium }}>{invoice.client_name}</span>
          {invoice.client_email ? ` · ${invoice.client_email}` : ''}
        </div>
      )}

      {/* Line items */}
      <div style={{ borderTop: `1px solid ${palette.g100}`, paddingTop: spacing.sm }}>
        {items.length === 0 ? (
          <div style={{ fontSize: typography.size.caption, color: palette.g400, padding: `${spacing.sm}px 0` }}>
            No line items.
          </div>
        ) : (
          items.map((li, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: spacing.md,
                padding: `${spacing.xs}px 0`,
                fontSize: typography.size.caption,
              }}
            >
              <div style={{ color: palette.g700 }}>
                {li.description}
                {li.quantity > 1 && (
                  <span style={{ color: palette.g500, marginLeft: 6, ...TABULAR }}>
                    × {li.quantity} @ {formatMoney(li.unit_price)}
                  </span>
                )}
              </div>
              <div style={{ color: palette.g800, fontWeight: typography.weight.medium, ...TABULAR }}>
                {formatMoney(li.amount)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Totals */}
      <div style={{ borderTop: `1px solid ${palette.g100}`, marginTop: spacing.sm, paddingTop: spacing.sm }}>
        <Row label="Subtotal" value={formatMoney(invoice.subtotal)} />
        {invoice.tax_amount > 0 && (
          <Row
            label={taxRatePct != null ? `Tax (${taxRatePct}%)` : 'Tax'}
            value={formatMoney(invoice.tax_amount)}
          />
        )}
        <Row
          label="Total"
          value={formatMoney(invoice.total)}
          strong
        />
      </div>

      {invoice.notes && (
        <div
          style={{
            marginTop: spacing.md,
            padding: spacing.sm,
            background: palette.g50,
            borderRadius: 8,
            fontSize: typography.size.caption,
            color: palette.g700,
            whiteSpace: 'pre-wrap',
          }}
        >
          {invoice.notes}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between',
        padding: `${spacing.xs}px 0`,
        fontSize: typography.size.caption,
        color: strong ? palette.g800 : palette.g600,
        fontWeight: strong ? typography.weight.semibold : typography.weight.regular,
      }}
    >
      <span>{label}</span>
      <span style={TABULAR}>{value}</span>
    </div>
  );
}
